import { useTranslation, translate } from '@rhino-project/core/i18n';
import { useMemo } from 'react';
import { Spinner, Typography } from '@heroui/react';
import { useModelIndex } from '@rhino-project/core/hooks';
import { useParams } from '@tanstack/react-router';
import { useActiveSeasons, usePlanningSeasons } from '../../hooks/ots';
import { useRosterStatuses } from '../../hooks/rosterstatus';
import { chipAccent } from '../shared/PlayerChips';
import { PlayerDetailLink } from './PlayerDetailLink';
import {
  buildProjectionGrid,
  UNASSIGNED_ROW_KEY,
  cellKey,
  playerTagNames,
  type ProjectionCell,
  type ProjectionColumn,
  type ProjectionEntry,
  type ProjectionPosition,
  type ProjectionRoster,
  type ProjectionRosterStatus,
  type ProjectionTeamSeason
} from './projectionGrid';

const resultsOf = <T,>(resources: unknown): T[] =>
  (resources as { results?: T[] } | undefined)?.results ?? [];

interface PlayerChipProps {
  entry: ProjectionEntry;
  owner: string;
  teamSeason: ProjectionTeamSeason;
}

const PlayerChip = ({ entry, owner, teamSeason }: PlayerChipProps) => {
  const { t } = useTranslation();

  const { player, isReturning } = entry;
  const tags = playerTagNames(player);

  // A returning player is already accounted for in the current roster, so they
  // recede; a new commit is the recruiting win and reads solid.
  const chipClass = isReturning
    ? 'border-dashed border-border-secondary bg-transparent'
    : 'border-border-secondary bg-surface shadow-surface';
  const nameClass = isReturning
    ? 'text-sm text-muted hover:underline'
    : 'text-sm font-medium hover:underline';

  return (
    <div
      className={`flex flex-wrap items-center gap-1 rounded-lg border px-2 py-1 ${chipClass}`}
    >
      <PlayerDetailLink
        owner={owner}
        teamSeason={teamSeason}
        playerId={String(player.id)}
        className={nameClass}
      >
        {player.display_name as string}
      </PlayerDetailLink>
      {isReturning && (
        <span
          className="text-xs text-muted"
          title={t('frontend.messages.already_on_the_roster')}
        >
          {t('frontend.messages.returning')}
        </span>
      )}
      {tags.map((tag) => (
        <span key={tag} className={chipAccent}>
          {tag}
        </span>
      ))}
    </div>
  );
};

// The two column kinds count different things, so each says what its number
// means rather than leaving a bare count to be read as comparable.
const columnCountLabel = (column: ProjectionColumn, total: number) =>
  column.kind === 'current'
    ? translate('frontend.messages.roster_count', { count: total })
    : translate('frontend.messages.committed_count', { count: total });

const cellCountLabel = (cell: ProjectionCell) =>
  cell.returning > 0
    ? translate('frontend.messages.returning_count', {
        count: cell.count,
        returning: cell.returning
      })
    : String(cell.count);

const RecruitingProjection = () => {
  const { t } = useTranslation();

  const { owner } = useParams({ from: '/_authenticated/$owner' });

  const { resources: planningSeasons, isLoading: isSeasonsLoading } =
    usePlanningSeasons();
  const { resources: activeSeasons, isLoading: isActiveSeasonsLoading } =
    useActiveSeasons();
  const { resources: planningStatuses, isLoading: isStatusesLoading } =
    useRosterStatuses({ status_type: 'planning' });
  const { resources: positions, isLoading: isPositionsLoading } = useModelIndex(
    'position',
    { networkOptions: { params: { limit: 1000 } } }
  );
  const teamSeasons = resultsOf<ProjectionTeamSeason>(planningSeasons);
  const activeTeamSeason =
    resultsOf<ProjectionTeamSeason>(activeSeasons)[0] ?? null;
  const statuses = resultsOf<ProjectionRosterStatus>(planningStatuses);
  // The recruiting pipeline's end state — the last planning lane, the one
  // Roster#notify_roster_accepted treats as "accepted a roster spot".
  const terminalStatus = statuses.length ? statuses[statuses.length - 1] : null;

  const teamSeasonIds = useMemo(
    () => teamSeasons.map((ts) => ts.id),
    [teamSeasons]
  );

  const rosterFilter = useMemo(
    () => ({
      team_season_id: teamSeasonIds,
      roster_status_id: terminalStatus?.id
    }),
    [teamSeasonIds, terminalStatus?.id]
  );

  const { resources: rosters, isLoading: isRostersLoading } = useModelIndex(
    'roster',
    {
      filter: rosterFilter,
      order: ['player.last_name', 'player.first_name'],
      queryOptions: {
        enabled: teamSeasonIds.length > 0 && !!terminalStatus?.id
      }
    }
  );

  // The current roster is this page's baseline only — deliberately a separate
  // query rather than widening the shared roster payload, which the recruiting
  // board fetches thousands of rows of.
  const activeRosterFilter = useMemo(
    () => ({
      team_season_id: activeTeamSeason?.id,
      roster_status: { status_type: 'active' }
    }),
    [activeTeamSeason?.id]
  );

  const { resources: activeRosters } = useModelIndex('roster', {
    filter: activeRosterFilter,
    order: ['player.last_name', 'player.first_name'],
    queryOptions: { enabled: !!activeTeamSeason?.id }
  });

  const positionResults = resultsOf<ProjectionPosition>(positions);

  const grid = useMemo(
    () =>
      buildProjectionGrid({
        activeTeamSeason,
        activeRosters: resultsOf<ProjectionRoster>(activeRosters),
        teamSeasons,
        rosters: resultsOf<ProjectionRoster>(rosters),
        positions: positionResults,
        terminalStatus
      }),
    [
      activeTeamSeason,
      activeRosters,
      teamSeasons,
      rosters,
      positionResults,
      terminalStatus
    ]
  );

  if (
    isSeasonsLoading ||
    isActiveSeasonsLoading ||
    isStatusesLoading ||
    isPositionsLoading
  ) {
    return <Spinner size="sm" className="mx-auto block" />;
  }

  if (!grid.columns.some((column) => column.kind === 'class')) {
    return (
      <p className="text-muted text-sm">
        {t(
          'frontend.messages.no_planning_seasons_yet_add_one_from_the_planning_scouting_dashboard'
        )}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {!isRostersLoading && !grid.hasCommitments && (
        <p className="text-muted text-sm">
          {t(
            'frontend.messages.nobody_has_accepted_a_spot_yet_every_class_below_is_still'
          )}
        </p>
      )}
      <div className="overflow-x-auto">
        {/* Classes share the width evenly and grow into it, the way the
            recruiting board's lanes do; the wrapper scrolls once enough
            classes are in play that they hit their minimum. */}
        <table className="w-full table-fixed border-separate border-spacing-1">
          <thead>
            <tr>
              <th
                className="w-28 p-2"
                aria-label={t('frontend.messages.position_group')}
              />
              {grid.columns.map((column) => (
                <th
                  key={column.key}
                  className="min-w-48 p-2 bg-surface-secondary rounded-t-3xl text-left"
                >
                  <Typography.Heading level={6}>
                    {column.label}
                  </Typography.Heading>
                  <span className="text-xs text-muted">
                    {columnCountLabel(column, grid.columnTotals[column.key])}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <tr key={row.key}>
                <th
                  scope="row"
                  className="p-2 text-left align-top whitespace-nowrap"
                >
                  <Typography.Heading level={6}>
                    {row.key === UNASSIGNED_ROW_KEY
                      ? t('frontend.messages.unassigned')
                      : row.label}
                  </Typography.Heading>
                </th>
                {grid.columns.map((column) => {
                  const cell = grid.cells[cellKey(row.key, column.key)];
                  return (
                    <td
                      key={column.key}
                      className="p-2 align-top bg-surface-secondary/50 rounded-b-3xl"
                    >
                      <span className="text-xs text-muted">
                        {cellCountLabel(cell)}
                      </span>
                      <div className="flex flex-col gap-1 mt-1">
                        {cell.entries.map((entry) => (
                          <PlayerChip
                            key={entry.roster.id as number}
                            entry={entry}
                            owner={owner}
                            // A returning player belongs to the current
                            // season's player page; only a new commit has
                            // nowhere but the Plan-mode one to go. Never hand
                            // the active branch a planning season — that would
                            // build /active/<planning-id>/…
                            teamSeason={
                              entry.isReturning && activeTeamSeason
                                ? activeTeamSeason
                                : column.teamSeason
                            }
                          />
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecruitingProjection;
